import {
  Button,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  cn,
} from '@up2stay/up2promo-ui';
import { MapPin } from 'lucide-react';
import { useMemo } from 'react';

export interface LocationComboboxOption {
  id: string;
  name: string;
}

interface LocationComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: LocationComboboxOption[];
  /** Value + label for the leading "all locations" entry. */
  allValue: string;
  allLabel: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Width/styling for the trigger button. */
  className?: string;
  disabled?: boolean;
}

type LocationPickerItem = {
  id: string;
  name: string;
};

/**
 * Searchable location picker used across Reputation screens.
 * Built on `@up2stay/up2promo-ui` Combobox (not a Command+Popover copy).
 */
export function LocationCombobox({
  value,
  onChange,
  options,
  allValue,
  allLabel,
  searchPlaceholder,
  emptyText,
  className,
  disabled,
}: LocationComboboxProps) {
  const items = useMemo<LocationPickerItem[]>(
    () => [{ id: allValue, name: allLabel }, ...options],
    [allValue, allLabel, options],
  );

  const selected = useMemo(
    () => items.find((item) => item.id === value) ?? items[0] ?? null,
    [items, value],
  );

  return (
    <Combobox
      items={items}
      value={selected}
      onValueChange={(next) => {
        if (next) onChange(next.id);
      }}
      itemToStringLabel={(item) => item.name}
      isItemEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled}
    >
      <ComboboxTrigger
        render={
          <Button
            variant="outline"
            className={cn('h-9 justify-between gap-2 font-normal', className)}
          />
        }
      >
        <span className="flex min-w-0 items-center gap-2">
          <MapPin className="text-muted-foreground h-4 w-4 shrink-0" />
          <span className="truncate">
            <ComboboxValue />
          </span>
        </span>
      </ComboboxTrigger>
      <ComboboxContent className="min-w-[14rem]" align="start">
        <ComboboxInput placeholder={searchPlaceholder} showTrigger={false} />
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: LocationPickerItem) => (
            <ComboboxItem key={item.id} value={item}>
              <span className="truncate">{item.name}</span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
